import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { combineLatest, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { EmptyStructuredQuery } from '../../../core/query/entities/empty-structured-query.constant';
import { ShUrlQueryService } from '../../../core/query/services/url-query.service';
import { UserPreferences } from '../../../core/users/entities/user-preferences.entity';
import { UserService } from '../../../core/users/services/user.service';
import { ActionMode } from '../../generic-overview/components/context-action-bar/entities/select-mode.entity';
import {
    createDisplayMode,
    DisplayMode
} from '../../generic-overview/components/display-settings-bar/entities/display-mode.entity';
import { SortInfo } from '../../generic-overview/components/display-settings-bar/entities/sort-info.entity';
import { PaginationInfo } from '../../generic-overview/components/pagination/entities/pagination-info.entity';
import { Panel, PartialSearchState, UrlQueryState } from '../entities/search-page-state.entity';

@Injectable()
export class SearchPageStateService {
    private readonly userService: UserService = inject(UserService);
    private readonly urlQueryService: ShUrlQueryService = inject(ShUrlQueryService);

    // User prefereneces
    private readonly $userPreferences = toSignal(this.userService.getUserPreferences());
    private readonly userPreferences$ = this.userService.getUserPreferences().pipe(shareReplay(1));

    // Signal holding current state (for the active page)
    private readonly $currentUrlState = this.urlQueryService.$urlQueryState;
    private readonly $currentSearchState = computed(() =>
        this.urlQueryToSearchState(this.$currentUrlState(), this.$userPreferences())
    );

    // Map of stored states per page key
    private readonly $storedUrlStates = signal<Record<string, UrlQueryState>>({});
    private readonly $storedSelectedStates = signal<Record<string, string[]>>({});
    private readonly $storedActionModeStates = signal<Record<string, ActionMode>>({});
    private readonly $storedPanelStates = signal<Record<string, Record<Panel, boolean>>>({});

    private readonly storedPanelStates$: Observable<Record<string, Record<Panel, boolean>>>;

    // Set which panels are open initially
    private readonly DEFAULT_PANEL_STATES: Record<Panel, boolean> = {
        [Panel.FILTERS]: true,
        [Panel.HIERARCHY]: true,
        [Panel.DETAIL]: false
    };

    /**
     * How does this work:
     *
     * Overviews should:
     *      1. Restore stored overview state on init
     *      2. Sync url store and state
     *      3. Consume current states
     *      4. When changes are made to selection, panels, pagination, view or sort they send it here to be stored
     *
     */

    constructor() {
        this.storedPanelStates$ = toObservable(this.$storedPanelStates);
    }

    public getUrlQueryState(): Observable<UrlQueryState> {
        return combineLatest([this.urlQueryService.getUrlQueryState(), this.userPreferences$]).pipe(
            map(([urlSearchPageState, userPreferences]) =>
                this.appendUrlQueryStateUserPreferences(urlSearchPageState, userPreferences)
            ),
            shareReplay(1)
        );
    }

    private appendUrlQueryStateUserPreferences(
        urlQueryState: UrlQueryState,
        userPreferences: UserPreferences
    ): UrlQueryState {
        const sortInfo = urlQueryState?.sortInfo ?? this.getUserSortPreferences(userPreferences);
        const view = urlQueryState?.view ?? this.getUserDisplayModePreferences(userPreferences);
        const pageSize = urlQueryState?.pageSize ?? this.getUserPageSizePreferences(userPreferences);

        return {
            ...urlQueryState,
            sortInfo,
            view,
            pageSize
        };
    }
    // #endregion

    // #region Url state
    public getSearchState(): Signal<PartialSearchState> {
        return this.$currentSearchState;
    }

    public saveUrlState(searchPageId: string): void {
        const current = this.$currentUrlState();
        if (!current) {
            return;
        }

        const copy = { ...this.$storedUrlStates() };
        copy[searchPageId] = current;
        this.$storedUrlStates.set(copy);
    }

    public restoreStoredUrlState(searchPageId: string): void {
        const stored = this.$storedUrlStates()[searchPageId];
        if (stored) {
            this.urlQueryService.setUrlQueryState(stored);
        }
    }

    public savePaginationState(paginationInfo: PaginationInfo): void {
        this.urlQueryService.setPaginationInfo(paginationInfo);
    }

    public saveSortState(sortInfo: SortInfo): void {
        this.urlQueryService.setSortInfo(sortInfo);
    }

    public saveViewState(view: DisplayMode): void {
        this.urlQueryService.setDisplayMode(view);
    }
    // #endregion

    // #region Selected
    public getStoredSelected(searchPageId: string): string[] {
        return this.$storedSelectedStates()[searchPageId] ?? [];
    }

    public getStoredActionMode(searchPageId: string): ActionMode {
        return this.$storedActionModeStates()[searchPageId] ?? [];
    }

    public saveSelectedState(searchPageId: string, selected: string[]): void {
        const fullState = { ...this.$storedSelectedStates() };
        fullState[searchPageId] = selected;
        this.$storedSelectedStates.set(fullState);
    }

    public saveActionModeState(searchPageId: string, actionMode: ActionMode): void {
        const fullState = { ...this.$storedActionModeStates() };
        fullState[searchPageId] = actionMode;
        this.$storedActionModeStates.set(fullState);
    }
    // #endregion

    // #region Panels
    public getPanelsState(searchPageId: string): Signal<Record<Panel, boolean>> {
        return computed(
            () =>
                this.$storedPanelStates()[searchPageId] ?? {
                    filters: true,
                    hierarchy: true,
                    detail: false
                }
        );
    }

    public getPanelStateStream(searchPageId: string): Observable<Record<Panel, boolean>> {
        return this.storedPanelStates$.pipe(map(storedPanelStates => storedPanelStates[searchPageId]));
    }

    public savePanelState(searchPageId: string, panel: Panel, open: boolean): void {
        const fullState = { ...this.$storedPanelStates() };
        const panelsState = fullState[searchPageId] ?? this.DEFAULT_PANEL_STATES;

        const updatedState: Record<Panel, boolean> = {
            ...panelsState,
            [panel]: open
        };

        fullState[searchPageId] = updatedState;
        this.$storedPanelStates.set(fullState);
    }
    // #endregion

    // #region Utils
    private urlQueryToSearchState(
        urlQueryState: UrlQueryState | null,
        userPreferences: UserPreferences | undefined
    ): PartialSearchState {
        const structuredQuery = urlQueryState?.structuredQuery ?? EmptyStructuredQuery;
        const sortInfo = urlQueryState?.sortInfo ?? this.getUserSortPreferences(userPreferences);
        const view = urlQueryState?.view ?? this.getUserDisplayModePreferences(userPreferences);
        const page = urlQueryState?.page ?? 0;
        const pageSize = urlQueryState?.pageSize ?? this.getUserPageSizePreferences(userPreferences);

        return {
            structuredQuery,
            sortInfo,
            view,
            page,
            pageSize
        };
    }

    private getUserSortPreferences(userPreferences: UserPreferences | undefined): SortInfo | undefined {
        return userPreferences?.DefaultSort ? userPreferences.DefaultSort : undefined;
    }

    private getUserDisplayModePreferences(userPreferences: UserPreferences | undefined): DisplayMode | undefined {
        return userPreferences?.DefaultDisplay ? createDisplayMode(userPreferences.DefaultDisplay) : undefined;
    }

    private getUserPageSizePreferences(userPreferences: UserPreferences | undefined): number | undefined {
        return userPreferences?.NrOfSearchResults ? Number.parseInt(userPreferences.NrOfSearchResults) : undefined;
    }
    // #endregion
}
